import { format as dateFnsFormat, formatDistance, formatRelative, type Locale } from 'date-fns';
import { enUS, bn } from 'date-fns/locale';

// Date-fns locale mapping
const dateLocales: Record<string, Locale> = {
  en: enUS,
  bn: bn,
  // Note: date-fns ships locales for English + Bengali only out of what we
  // support. Languages without a date-fns locale (lo / km / my / sw / ur /
  // tl / vi / th / am) fall back to enUS for date-fns formatting. Numbers,
  // times, and percentages still go through Intl.NumberFormat / Intl.DateTimeFormat
  // below, which DO have lo-LA + km-KH + etc. coverage natively.
  lo: enUS,
  km: enUS,
  my: enUS,
  sw: enUS,
  ur: enUS,
  tl: enUS,
  vi: enUS,
  th: enUS,
  am: enUS,
};

// Full locale codes for Intl APIs
export const localeCodeMap: Record<string, string> = {
  en: 'en-US',
  my: 'my-MM', // Myanmar (Burmese)
  bn: 'bn-BD', // Bangladesh (Bengali)
  sw: 'sw-TZ', // Tanzania (Swahili)
  km: 'km-KH', // Cambodia (Khmer)
  lo: 'lo-LA', // Laos (Lao)
  ur: 'ur-PK', // Pakistan (Urdu)
  tl: 'tl-PH', // Philippines (Filipino)
  vi: 'vi-VN', // Vietnam (Vietnamese)
  th: 'th-TH', // Thailand (Thai)
  am: 'am-ET', // Ethiopia (Amharic)
};

/**
 * Format a date according to the user's locale
 */
export const formatDate = (
  date: Date | string | number,
  formatStr: string = 'PPP',
  languageCode: string = 'en'
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const locale = dateLocales[languageCode] || dateLocales.en;
  
  return dateFnsFormat(dateObj, formatStr, { locale });
};

/**
 * Format a date relative to now (e.g., "2 hours ago")
 */
export const formatRelativeDate = (
  date: Date | string | number,
  baseDate: Date = new Date(),
  languageCode: string = 'en'
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const locale = dateLocales[languageCode] || dateLocales.en;
  
  return formatRelative(dateObj, baseDate, { locale });
};

/**
 * Format distance between dates (e.g., "2 hours")
 */
export const formatDateDistance = (
  date: Date | string | number,
  baseDate: Date = new Date(),
  languageCode: string = 'en',
  addSuffix: boolean = true
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const locale = dateLocales[languageCode] || dateLocales.en;
  
  return formatDistance(dateObj, baseDate, { locale, addSuffix });
};

/**
 * Format a number according to the user's locale
 */
export const formatNumber = (
  value: number,
  languageCode: string = 'en',
  options?: Intl.NumberFormatOptions
): string => {
  const locale = localeCodeMap[languageCode] || localeCodeMap.en;
  
  return new Intl.NumberFormat(locale, options).format(value);
};

/**
 * Format currency for the farmer's country.
 *
 * Number grouping/decimals/placement come from Intl (driven by the ISO
 * currencyCode), but the displayed glyph is the DB-provided currencySymbol
 * (countries.currency_symbol) so the symbol matches exactly what MAP configured
 * — Intl would otherwise emit the ISO code for some currencies (e.g. "AMD"
 * instead of ֏). No hardcoded map, no BDT/USD default.
 *
 * When no currency is resolved yet (e.g. the country row is still loading),
 * renders a neutral grouped number with NO symbol rather than guessing a wrong
 * currency.
 */
export const formatCurrency = (
  value: number,
  languageCode: string = 'en',
  currencyCode?: string | null,
  currencySymbol?: string | null
): string => {
  const locale = localeCodeMap[languageCode] || localeCodeMap.en;

  if (!currencyCode) {
    return new Intl.NumberFormat(locale).format(value);
  }

  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    }).formatToParts(value);

    return parts
      .map((p) => (p.type === 'currency' ? (currencySymbol || p.value) : p.value))
      .join('');
  } catch {
    const num = new Intl.NumberFormat(locale).format(value);
    return currencySymbol ? `${currencySymbol}${num}` : num;
  }
};

/**
 * Format percentage according to the user's locale
 */
export const formatPercent = (
  value: number,
  languageCode: string = 'en',
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 2
): string => {
  const locale = localeCodeMap[languageCode] || localeCodeMap.en;
  
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

/**
 * Format compact number (e.g., 1000 -> 1K)
 */
export const formatCompactNumber = (
  value: number,
  languageCode: string = 'en'
): string => {
  const locale = localeCodeMap[languageCode] || localeCodeMap.en;
  
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
};

/**
 * Format a time value
 */
export const formatTime = (
  date: Date | string | number,
  languageCode: string = 'en',
  includeSeconds: boolean = false
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const locale = localeCodeMap[languageCode] || localeCodeMap.en;
  
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
  }).format(dateObj);
};
